import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as AssignUser } from '../../../../../images/icons/assign_user.svg';
import {
  active,
  backgroundActive,
  dimGrey,
  inactive,
  lightBlack,
  lightGrey,
  lightTeal,
} from '../../../../../mainTheme';

// Constants
const COLORS = {
  active: active,
  inactive: inactive,
  text: {
    primary: lightBlack,
    secondary: dimGrey,
    tertiary: lightGrey,
  },
  background: {
    active: backgroundActive,
    inactive: '#F6F6F9',
  },
} as const;

// Types
interface PatientTypeProps {
  onNext: (type: string) => void;
  onCancel: () => void;
}

interface RadioOptionProps {
  value: string;
  title: string;
  description: string;
  isSelected: boolean;
}

// Reusable Components
const StyledTypography: React.FC<{
  variant: 'title' | 'subtitle' | 'description';
  children: React.ReactNode;
}> = ({ variant, children }) => {
  const styles = {
    title: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.2px',
      color: COLORS.text.primary,
    },
    subtitle: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '22px',
      letterSpacing: '0.2px',
      color: COLORS.text.tertiary,
    },
    description: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      color: COLORS.text.secondary,
    },
  };

  return <Typography sx={styles[variant]}>{children}</Typography>;
};

const RadioOption: React.FC<RadioOptionProps> = ({
  value,
  title,
  description,
  isSelected,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderRadius: '8px',
      borderColor: isSelected ? COLORS.active : COLORS.inactive,
      p: 1,
      mb: 1,
      backgroundColor: isSelected
        ? COLORS.background.active
        : COLORS.background.inactive,
    }}
  >
    <FormControlLabel
      value={value}
      sx={{
        margin: 0,
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiFormControlLabel-label': { flex: 1 },
      }}
      control={
        <Radio
          sx={{
            '&.MuiRadio-root': {
              color: isSelected ? lightTeal : COLORS.text.tertiary,
            },
          }}
        />
      }
      labelPlacement="start"
      label={
        <Box>
          <StyledTypography variant="title">{title}</StyledTypography>
          <StyledTypography variant="description">
            {description}
          </StyledTypography>
        </Box>
      }
    />
  </Box>
);

export const PatientTypeSelection: React.FC<PatientTypeProps> = ({
  onNext,
  onCancel,
}) => {
  const [patientType, setPatientType] = useState('new');

  const radioOptions = [
    {
      value: 'new',
      title: 'New',
      description: 'This requires full patient details.',
    },
    {
      value: 'existing',
      title: 'Existing',
      description: 'This requires patient name and date of birth.',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ProgressBar currentStep={1} totalSteps={2} />
      <AssignUser />
      <Box>
        <StyledTypography variant="title">
          Is the patient existing or new?
        </StyledTypography>
        <StyledTypography variant="subtitle">
          This requires patient details.
        </StyledTypography>
      </Box>
      <RadioGroup
        value={patientType}
        onChange={(e) => setPatientType(e.target.value)}
      >
        {radioOptions.map((option) => (
          <RadioOption
            key={option.value}
            {...option}
            isSelected={patientType === option.value}
          />
        ))}
      </RadioGroup>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="text" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onNext(patientType)}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
